import * as React from 'react';

import { useIntl } from 'react-intl';
import { throttle } from 'lodash';

import {
    LIST_EVENTS_HOME_STATISTICS_RESOURCE,
    useListEventsHomeStatistics,
} from 'src/features/home/api/useListEventHomeStatistics';
import { LIST_HOME_STATISTICS_RESOURCE, useListHomeStatistics } from 'src/features/home/api/useListHomeStatistics';
import WidgetEntries from 'src/features/home/components/WidgetEntries';
import WidgetEvents from 'src/features/home/components/WidgetEvents';
import WidgetExits from 'src/features/home/components/WidgetExits';
import WidgetOccupancy from 'src/features/home/components/WidgetOccupancy';
import WidgetVisits from 'src/features/home/components/WidgetVisits';
import WidgetZones from 'src/features/home/components/WidgetZones';
import { homeMessages } from 'src/features/home/utils/messages';
import { useTitleStore } from 'src/stores/useTitleStore';
import WSContext, { WebSocketGroup } from 'src/features/visitor/context/WSContext';
import { WebSocketEvent, WebSocketEventMessages } from 'src/features/visitor/types';
import { queryClient } from 'src/lib/react-query';
import { useParking } from 'src/providers/ParkingProvider';

const GATEWAY_EVENTS = [
    WebSocketEventMessages.NEW_LPR_DETECTION,
    WebSocketEventMessages.NEW_ACTIVE_STAY,
    WebSocketEventMessages.NEW_ACTIVE_STAY_LOG_EVENT,
    WebSocketEventMessages.NEW_ACTIVE_STAY_PERIOD,
    WebSocketEventMessages.NEW_ACTIVE_STAY_PERIOD_LOG_EVENT,
    WebSocketEventMessages.COMPLETED_ACTIVE_STAY,
    WebSocketEventMessages.COMPLETED_ACTIVE_STAY_LOG_EVENT,
    WebSocketEventMessages.NEW_ENTRY_PASS,
    WebSocketEventMessages.NEW_EXIT_PASS,
    WebSocketEventMessages.COMPLETED_ACTIVE_STAY_PERIOD,
    WebSocketEventMessages.COMPLETED_ACTIVE_STAY_PERIOD_LOG_EVENT,
];
export default function Home() {
    const { formatMessage } = useIntl();
    const { setTitle, removeTitle } = useTitleStore();
    const { subscribe, unsubscribe } = React.useContext(WSContext);

    React.useEffect(() => {
        setTitle(formatMessage(homeMessages.dashboard));
        return removeTitle;
    }, [formatMessage, removeTitle, setTitle]);

    const handleWebSocket = React.useCallback(
        throttle((event: WebSocketEvent) => {
            if (GATEWAY_EVENTS.includes(event.message)) {
                void queryClient.invalidateQueries({ queryKey: [LIST_HOME_STATISTICS_RESOURCE] });
                void queryClient.invalidateQueries({ queryKey: [LIST_EVENTS_HOME_STATISTICS_RESOURCE] });
            }
        }, 500),
        [],
    );

    React.useEffect(() => {
        subscribe(WebSocketGroup.GATEWAY_PASS, handleWebSocket);

        return () => unsubscribe(WebSocketGroup.GATEWAY_PASS, handleWebSocket);
    }, []);

    const { parkingId } = useParking();
    const { data: homeStatistics, isLoading, isError } = useListHomeStatistics({ parkingId });
    const { data: eventHomeStatistics, isError: isEventError } = useListEventsHomeStatistics({ parkingId });

    return (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
            <WidgetZones
                statsByZones={homeStatistics?.stats_by_zones}
                totalStatsByZones={homeStatistics?.total_stats_for_zones}
                isLoading={isLoading}
                isError={isError}
            />
            <WidgetOccupancy totalOccupancy={homeStatistics?.total_occupancy} isLoading={isLoading} isError={isError} />
            <WidgetVisits
                isLoading={isLoading}
                membershipVisits={homeStatistics?.membership_visits}
                totalVisits={homeStatistics?.total_visits}
                isError={isError}
            />
            <WidgetEvents
                data={eventHomeStatistics?.last_events}
                totalEvents={eventHomeStatistics?.total_events}
                isError={isEventError}
            />
            <WidgetEntries entries={homeStatistics?.entries} isError={isError} />
            <WidgetExits exits={homeStatistics?.exits} isError={isError} />
        </div>
    );
}
